import LabelIcon from '@mui/icons-material/LabelOutlined';
import { Box, Button, Checkbox, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import React from 'react';
import { useDataContext } from '../context/DataContext';
import { useUIContext } from '../context/UIContext';
import { SiteCard } from './SiteCard';
import { EmptyBookmarksIllustration } from './Illustrations';
import { ModifyBookmarkDialog } from './ModifyBookmarkDialog';
import { AboutDialog } from './AboutDialog';
import { UploadDialog } from './UploadDialog';
import { BulkTagDialog } from './BulkTagDialog';
import { useLocation } from 'react-router';


interface BookmarksGridProps {
  dialog?: string;
}

export const BookmarksGrid: React.FC<BookmarksGridProps> = ({ dialog }) => {

  const { allBookmarks, loadMoreBookmarks, hasMoreBookmarks, isLoadingMore } = useDataContext();
  const { loadingBookmarks } = useUIContext();
  const location = useLocation();
  const loadMoreRef = React.useRef<HTMLDivElement>(null);
  const [selectedBookmarkIds, setSelectedBookmarkIds] = React.useState<string[]>([]);
  const [bulkDialogOpen, setBulkDialogOpen] = React.useState(false);

  /*
  If the contents of allBookmarks changes (and it's a new search/reset), scroll to the top of the grid.
  */
  const prevBookmarksLengthRef = React.useRef<number>(0);
  React.useEffect(() => {
    const grid = document.getElementById('bookmarks-grid');
    if (grid && allBookmarks && allBookmarks.length < prevBookmarksLengthRef.current) {
      // If bookmarks decreased, it's likely a new search/reset
      grid.scrollTo({ top: 0, behavior: 'smooth' });
    }
    prevBookmarksLengthRef.current = allBookmarks?.length || 0;
  }, [allBookmarks]);

  React.useEffect(() => {
    setSelectedBookmarkIds([]);
  }, [location.pathname, location.search]);

  React.useEffect(() => {
    if (!allBookmarks) {
      setSelectedBookmarkIds([]);
      return;
    }
    const availableIds = new Set(allBookmarks.map(bookmark => bookmark.fid).filter((bookmarkId): bookmarkId is string => Boolean(bookmarkId)));
    setSelectedBookmarkIds(prev => prev.filter(bookmarkId => availableIds.has(bookmarkId)));
  }, [allBookmarks]);

  // Infinite scroll: observe when the load more trigger element is visible
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreBookmarks && !isLoadingMore) {
          loadMoreBookmarks();
        }
      },
      {
        root: null,
        rootMargin: '200px', // Start loading 200px before reaching the bottom
        threshold: 0.1,
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMoreBookmarks, isLoadingMore, loadMoreBookmarks]);

  const isEmpty = allBookmarks !== null && allBookmarks.length === 0 && !loadingBookmarks;

  const toggleBookmarkSelected = (fid: string) => {
    setSelectedBookmarkIds(prev => prev.includes(fid) ? prev.filter(bookmarkId => bookmarkId !== fid) : [...prev, fid]);
  };

  const visibleBookmarkIds = allBookmarks === null ? [] : allBookmarks
    .map(bookmark => bookmark.fid)
    .filter((bookmarkId): bookmarkId is string => Boolean(bookmarkId));

  const selectedBookmarks = allBookmarks === null ? [] : allBookmarks.filter(bookmark => bookmark.fid && selectedBookmarkIds.includes(bookmark.fid));
  const allVisibleSelected = visibleBookmarkIds.length > 0 && visibleBookmarkIds.every(bookmarkId => selectedBookmarkIds.includes(bookmarkId));
  const selectionMode = selectedBookmarkIds.length > 0;

  const cards = allBookmarks === null ? [] : allBookmarks.map((bookmark, index) => {
    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={`bookmark-${index}`}>
        <SiteCard
          site={bookmark}
          selectionMode={selectionMode}
          selected={bookmark.fid ? selectedBookmarkIds.includes(bookmark.fid) : false}
          onToggleSelected={toggleBookmarkSelected}
        />
      </Grid>
    )
  });

  return (
    <Box>
      {selectedBookmarkIds.length > 0 && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{
            position: 'sticky',
            top: 72,
            zIndex: 1,
            mt: 2,
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            backgroundColor: 'background.paper',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox
              checked={allVisibleSelected}
              indeterminate={!allVisibleSelected && selectedBookmarkIds.length > 0}
              onChange={() => setSelectedBookmarkIds(allVisibleSelected ? [] : visibleBookmarkIds)}
            />
            <Typography variant="body2">
              {selectedBookmarkIds.length} selected
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setSelectedBookmarkIds([])} variant="outlined">Clear</Button>
            <Button
              onClick={() => setBulkDialogOpen(true)}
              variant="contained"
              color="secondary"
              startIcon={<LabelIcon />}
            >
              Edit tags
            </Button>
          </Stack>
        </Stack>
      )}
      {isEmpty ? (
        <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 10 }}>
          <EmptyBookmarksIllustration />
          <Typography variant="h6" color="text.secondary">No bookmarks yet</Typography>
          <Typography variant="body2" color="text.secondary">Tap + to save your first link</Typography>
        </Stack>
      ) : (
      <Grid container spacing={2.5} sx={{ py: 2 }}>
        {cards}
      </Grid>
      )}
      {/* Infinite scroll trigger element */}
      {hasMoreBookmarks && (
        <Box
          ref={loadMoreRef}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 3,
            minHeight: '100px',
          }}
        >
          {isLoadingMore && <CircularProgress size={40} />}
        </Box>
      )}
      <ModifyBookmarkDialog open={dialog == 'modify'} />
      <AboutDialog open={dialog == 'about'} />
      <UploadDialog open={dialog == 'pocket'} />
      <BulkTagDialog
        open={bulkDialogOpen}
        selectedBookmarks={selectedBookmarks}
        onClose={() => setBulkDialogOpen(false)}
        onSaved={() => {
          setBulkDialogOpen(false);
          setSelectedBookmarkIds([]);
        }}
      />
    </Box>
  );
};
